import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PropsWithChildren } from 'react';
import { type ClassName, twx } from '@/utils/tailwind';

const switchSizes = ['sm', 'base'] as const;
export type SwitchSize = (typeof switchSizes)[number];

interface SwitchSizeSpec {
  track: string;
  knob: string;
  knobOff: string;
  knobOn: string;
}

const sizeSpecs: Record<SwitchSize, SwitchSizeSpec> = {
  sm: {
    track: 'h-4 w-7',
    knob: 'h-3 w-3',
    knobOff: 'translate-x-0.5',
    knobOn: 'translate-x-[14px]',
  },
  base: {
    track: 'h-5 w-9',
    knob: 'h-4 w-4',
    knobOff: 'translate-x-0.5',
    knobOn: 'translate-x-[18px]',
  },
};

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

export interface SwitchProps extends PropsWithChildren {
  /** Current on/off state. Controlled — caller owns the value. */
  checked: boolean;
  onChange: (next: boolean) => void;
  /** Optional leading icon that switches color tone based on `checked`. */
  icon?: IconDefinition;
  size?: SwitchSize;
  /** Accessible label when no `children` are rendered (icon-only switches). */
  ariaLabel?: string;
  className?: ClassName;
  /** Disabled state — input rejects toggles and the knob track desaturates. */
  disabled?: boolean;
}

/**
 * Pill-shaped on/off control with an optional leading icon + label.
 *
 * The track color, knob position, and icon tone all swap in unison so the
 * "on" state reads at a glance. Sizes:
 *   - `sm` — compact: lives inside dense rows
 *   - `base` — default: menu rows (matches TripModeMenu's other items)
 */
export const Switch = ({
  checked,
  onChange,
  icon,
  children,
  size = 'base',
  ariaLabel,
  className,
  disabled = false,
}: SwitchProps) => {
  const spec = sizeSpecs[size];
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={!children ? ariaLabel : undefined}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={twx(
        'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-sm text-content-text hover:bg-content-focal transition-colors cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
        className,
      )}
    >
      <span className="flex items-center gap-2">
        {icon && (
          <FontAwesomeIcon
            icon={icon}
            style={
              {
                width: 14,
                height: 14,
                '--fa-primary-color': checked
                  ? 'var(--color-primary-emphasized)'
                  : 'var(--color-content-subtle)',
                '--fa-secondary-color': checked
                  ? 'var(--color-primary-main)'
                  : 'var(--color-content-deemphasized)',
                '--fa-secondary-opacity': '1',
              } as FAIconStyle
            }
          />
        )}
        {children}
      </span>
      <span
        className={twx(
          'relative inline-flex items-center rounded-full transition-colors shrink-0',
          spec.track,
          checked ? 'bg-primary-emphasized' : 'bg-content-border',
        )}
      >
        <span
          className={twx(
            'inline-block rounded-full bg-white transition-transform',
            spec.knob,
            checked ? spec.knobOn : spec.knobOff,
          )}
        />
      </span>
    </button>
  );
};
